import React, { ComponentProps } from "react";
import styled, { css, SimpleInterpolation } from "styled-components";

import { Button, ButtonProps } from "../Button";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { TruncatedMultiLineText } from "../TruncatedMultiLineText";
import { mediaQueries, style } from "../style";
import { focusOutline } from "../utils/focusOutlineStyle";
import { Heading } from "../Heading";

const CARD_SPACING_LG = style.vars.space["4"]; // 24px
const CARD_SPACING_MD = style.vars.space["3"]; // 16px
const CARD_SPACING_SM = style.vars.space["2"]; // 8px
const CARD_SPACING_XS = style.vars.space["1"]; // 4px
const CARD_BORDER = `1px solid ${style.colors.grey.light}`;

const cardStyles = (): SimpleInterpolation => {
  return css`
    border-radius: ${style.vars.borderRadius.base};
    box-shadow: ${style.vars.boxShadow.base};
    transition: box-shadow ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};
  `;
};

type StyledCardProps = {
  $backgroundColor?: string;
  $backgroundImage?: string;
  $raiseOnHover?: boolean;
};

const StyledCard = styled.section<StyledCardProps>`
  ${cardStyles()}

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: ${CARD_SPACING_MD};

  background-color: ${(props) => props.$backgroundColor ?? style.colors.white};
  ${({ $backgroundImage, $backgroundColor }) =>
    $backgroundImage &&
    css`
      /* Only display background image on medium+ viewports. */
      ${mediaQueries.mediumMin} {
        background: no-repeat ${$backgroundColor ?? style.colors.white} url(${$backgroundImage}) bottom right;
        /* Prevents the CardContent text from overlapping with the image
        and being unreadable. */
        ${CardContent} {
          padding-right: 120px;
        }
      }
    `}

  ${({ $raiseOnHover }) =>
    $raiseOnHover &&
    css`
      &:hover {
        box-shadow: ${style.vars.boxShadow.raised};
      }
    `}
`;

type CardProps = {
  backgroundColor?: string;
  backgroundImage?: string;
  /**
   * Adds box-shadow styles that visually raise the Card on hover state
   * */
  raiseOnHover?: boolean;
  className?: string;
};

export const Card: React.FC<CardProps> = ({ backgroundColor, backgroundImage, raiseOnHover = false, children, className }) => {
  return (
    <StyledCard $backgroundColor={backgroundColor} $backgroundImage={backgroundImage} $raiseOnHover={raiseOnHover} className={className}>
      {children}
    </StyledCard>
  );
};

export const CardHeaderRow = styled.div`
  background: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: ${CARD_SPACING_LG} ${CARD_SPACING_LG} ${CARD_SPACING_MD} ${CARD_SPACING_LG};
  width: 100%;
`;

export const CardFooterRow = styled.div`
  background: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  padding: ${CARD_SPACING_SM} ${CARD_SPACING_LG};
  border-top: ${CARD_BORDER};
  width: 100%;

  > * {
    &:not(:last-child) {
      margin-right: ${CARD_SPACING_MD};
    }
  }
`;

export const CardContent = styled.div`
  background: transparent;
  padding: 0 ${CARD_SPACING_LG} ${CARD_SPACING_MD} ${CARD_SPACING_LG};
  width: 100%;

  &:first-child {
    padding-top: ${CARD_SPACING_LG};
  }

  &:last-child {
    padding-bottom: ${CARD_SPACING_LG};
  }

  > * {
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const CardList = styled.ul`
  margin: 0;
  padding: 0;
  width: 100%;
`;

export const CardListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  list-style: none;
  padding: ${CARD_SPACING_XS} ${CARD_SPACING_LG};
  width: 100%;

  &:not(:last-child) {
    border-bottom: ${CARD_BORDER};
  }
`;

type CardHeaderProps = {
  inverted?: boolean;
  level?: ComponentProps<typeof Heading>["level"];
  color?: ComponentProps<typeof Heading>["color"];
};
export const CardHeader: React.FC<CardHeaderProps> = ({ inverted, children, level = "h5", color }) => {
  const _color = inverted ? "white" : color;
  return (
    <Heading weight="bold" level={level} color={_color}>
      {children}
    </Heading>
  );
};

const CloseButton = styled(Button)`
  margin: -14px -12px 0 0;
`;

export const CardCloseButton: React.FC<ButtonProps> = (props) => {
  return (
    <CloseButton compact {...props}>
      <Icon icon="close" />
    </CloseButton>
  );
};

export const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(368px, 1fr));
  column-gap: ${CARD_SPACING_MD};
`;

export const CardGridColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0;

  > ${StyledCard} {
    margin: calc(${CARD_SPACING_MD} / 2) 0;
  }
`;

const CardLinkContainer = styled.a`
  min-width: 230px;
  padding: ${CARD_SPACING_LG};
  display: block;

  ${cardStyles()}

  &:hover,
  &:focus {
    ${focusOutline()}
  }
`;

const CardLinkTitle = styled(Heading).attrs({ level: "h5" })``;

const CardLinkSubtitle = styled(Text)`
  color: ${style.colors.primary.base};
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.bold};
  margin-top: 0;
`;

const CardLinkContent = styled(TruncatedMultiLineText).attrs({
  as: "p",
  lines: 4,
})`
  color: ${style.colors.grey.dark};
  margin: 0;
`;

type Props = {
  title: string;
  subtitle: string;
  destination: string;
  className?: string;
  children: React.ReactElement | string;
};

export const CardLink: React.FC<Props> = ({ title, subtitle, destination, className, children }) => {
  return (
    <CardLinkContainer className={className} href={destination} target="_blank" rel="noopener noreferrer" tabIndex={0}>
      <CardLinkTitle>{title}</CardLinkTitle>
      <CardLinkSubtitle>{subtitle}</CardLinkSubtitle>
      <CardLinkContent>{children}</CardLinkContent>
    </CardLinkContainer>
  );
};
